/**
 * Authentication Store — React-native auth lifecycle.
 *
 * Source of truth for session state, username, and resolved permissions.
 * Runs identically with or without AngularJS on the page; the same backend
 * RoleMaster + Permissions APIs are used in both modes.
 *
 * Concurrency: `initAuth()` deduplicates concurrent callers via an in-flight
 * promise (no polling, no setTimeout race).
 *
 * Standalone-only: the store no longer mirrors permissions to
 * `window.G_PERMISSIONS`. That mirror existed for an AngularJS shell that no
 * longer ships with the standalone build. Embedded mode still gets its
 * `window.G_PERMISSIONS` write from `EmbeddedLegacyPermissionsProvider` in
 * `useLegacyPermissionsImpl.tsx` (which only runs in embedded mode).
 */

import { create } from 'zustand';
import { validateSession, clearAuthCache, redirectToLogin } from '@/lib/authApi';
import { fetchAndResolvePermissions } from '@/lib/permissionsApi';

// ============================================================================
// TYPES
// ============================================================================

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthState {
  /** Current auth lifecycle status */
  status: AuthStatus;
  /** Whether the user has a valid session */
  isAuthenticated: boolean;
  /** Resolved username (from RoleMaster session response) */
  username: string | null;
  /** Resolved G_PERMISSIONS-style permissions object */
  permissions: Record<string, unknown>;
  /** Error message if auth initialization failed */
  error: string | null;
}

interface AuthActions {
  /**
   * Initialize authentication.
   *
   * 1. Validate the session cookie via RoleMaster
   * 2. If valid: fetch and resolve permissions
   *
   * Concurrent calls share a single in-flight promise — no race, no polling.
   *
   * @param force Bypass the 30-second RoleMaster cache TTL
   * @returns true if authenticated, false otherwise
   */
  initAuth: (force?: boolean) => Promise<boolean>;

  /**
   * Handle a session-expired (401) signal: clear caches, mark unauthenticated,
   * and redirect to the login page.
   */
  handleSessionExpired: () => void;

  /**
   * Log the user out: clear caches, clear local state, then redirect to login.
   * The RoleMaster cookie is cleared by hitting the auth-host logout endpoint
   * (best effort — failure does not block the local cleanup).
   */
  logout: () => Promise<void>;

  /** Reset auth state to idle (used in tests and dev). */
  resetAuth: () => void;

  /** Replace permissions in the store. */
  setPermissions: (permissions: Record<string, unknown>) => void;

  /**
   * Merge a partial permissions object into the existing permissions map.
   * Used by `checkPermissions(spec)` to apply route-level permission resolution
   * on top of the boot-time permissions without replacing the whole map.
   */
  mergePermissions: (partial: Record<string, unknown>) => void;
}

type AuthStore = AuthState & AuthActions;

// ============================================================================
// HELPERS (kept module-scoped so tests can rely on a stable contract)
// ============================================================================

const INITIAL_STATE: AuthState = {
  status: 'idle',
  isAuthenticated: false,
  username: null,
  permissions: {},
  error: null,
};

/**
 * Best-effort logout API call. Constructs the logout URL from the same base
 * domain logic as the login page (authentication.{baseDomain}/logout).
 */
async function callLogoutEndpoint(): Promise<void> {
  const hostname = window.location.hostname || '';
  const lastDot = hostname.lastIndexOf('.');
  if (lastDot === -1 || hostname === 'localhost') return;
  const secondLastDot = hostname.lastIndexOf('.', lastDot - 1);
  const base = secondLastDot === -1 ? hostname : hostname.substring(secondLastDot + 1);
  const url = `https://authentication.${base}/logout`;

  try {
    await fetch(url, { method: 'GET', credentials: 'include', mode: 'no-cors' });
  } catch {
    // Best effort. Cookie may still be cleared server-side via separate flow.
  }
}

// ============================================================================
// STORE
// ============================================================================

/** Module-scoped in-flight promise for `initAuth` deduplication. */
let inFlightInit: Promise<boolean> | null = null;

export const useAuthStore = create<AuthStore>()((set) => ({
  ...INITIAL_STATE,

  initAuth: (force = false) => {
    if (inFlightInit && !force) return inFlightInit;

    set({ status: 'loading', error: null });

    inFlightInit = (async () => {
      try {
        // Step 1 — validate session cookie via RoleMaster. This is the first
        // await so the network call fires synchronously when initAuth() runs
        // (the concurrent-call dedup test relies on that).
        let sessionResult = await validateSession(force);

        // reauth helpers — imported after the first validate so the dynamic
        // import doesn't delay the network call. `reauthInProgress()` tells us
        // we just came back from a Windows Auth bounce so we know to
        // retry-with-backoff; `beginReauth()` is the recovery bounce;
        // `markReauthSuccess()` clears the loop-guard counter once we're
        // authenticated again.
        const { reauthInProgress, beginReauth, markReauthSuccess } = await import('@/lib/reauth');

        // Right after a Windows Auth bounce-back the IdP has set the cookie,
        // but RoleMaster's session store can lag it by a beat — the first
        // getfunctionsbytoken can still 401 even though the cookie is good.
        // Retry a few times with a short backoff before concluding the
        // session is dead. This is what made the user see "401 on login,
        // works after many tries" — now the first try absorbs the lag.
        //
        // Widened from 3x700ms (~2s) to 5x1000ms (~5s) as cheap insurance
        // against slower session-store propagation observed in deployed
        // dev2. 5s is still short enough that real failures surface in time
        // for the user to retry.
        if (!sessionResult.authed && reauthInProgress()) {
          for (let attempt = 0; attempt < 5 && !sessionResult.authed; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            clearAuthCache();
            sessionResult = await validateSession(true);
          }
        }

        if (!sessionResult.authed) {
          // First-visit short-circuit: a browser that has NEVER been signed in
          // here (no `currentUser` cached from a prior successful auth) should
          // not attempt a silent Windows Auth bounce — there's no IdP identity
          // to recover, the bounce just dead-ends on the auth host (or loops)
          // and the user never sees our /login page. Skip straight to
          // `unauthenticated` so AuthGate routes to /login, where the explicit
          // "Sign in as Staff" button starts the Windows Auth flow.
          //
          // Mid-session expiry (cookie was good, then went stale) is unchanged:
          // `currentUser` is present, so we still bounce for silent SSO.
          let hasPriorSession = false;
          try {
            hasPriorSession = !!localStorage.getItem('currentUser');
          } catch {
            // localStorage blocked — treat as first visit
          }
          if (!hasPriorSession && !reauthInProgress()) {
            set({
              ...INITIAL_STATE,
              status: 'unauthenticated',
            });
            return false;
          }

          // Session is dead. Recover by bouncing through Windows Auth — the
          // user keeps their route (AuthGate restores it after bounce-back).
          // beginReauth() resolves true while the page navigates away; it
          // resolves false only when it CAN'T recover (loop guard tripped
          // after repeated failures, pure localhost, embedded, or vendor
          // context) — then we fall through to `unauthenticated` so AuthGate
          // sends the user to /login.
          const navigating = await beginReauth();
          if (navigating) {
            // Page is leaving — hold the loading state, do NOT flip to
            // `unauthenticated` (that would flash /login before the bounce).
            return false;
          }
          set({
            ...INITIAL_STATE,
            status: 'unauthenticated',
          });
          return false;
        }

        // Authenticated — clear the re-auth loop-guard counter.
        markReauthSuccess();

        // Step 2 — fetch and resolve permissions.
        //
        // Failure policy (Phase B.1):
        //   • Production (`!import.meta.env.DEV`): fail-closed. An
        //     authenticated session without a permission map is an invalid
        //     state — surface a hard error so the user sees a clear
        //     "contact administrator" page instead of a half-rendered app.
        //   • Dev: fail-open. A backend AD-group misconfiguration shouldn't
        //     block a developer from working on unrelated features. The empty
        //     permission map is logged so the gap is visible.
        let resolvedPermissions: Record<string, unknown> = {};
        let permissionsFailed = false;
        try {
          resolvedPermissions = await fetchAndResolvePermissions();
        } catch (err) {
          permissionsFailed = true;
          console.warn('[authStore] Permissions fetch failed.', err);
        }

        if (permissionsFailed && !import.meta.env.DEV) {
          set({
            ...INITIAL_STATE,
            status: 'error',
            error: 'Could not load your permissions. Please contact an administrator.',
          });
          return false;
        }

        // Step 3 — extract username from RoleMaster response if it exposes one.
        // RoleMaster returns { functions, ...maybeMore }. Username may live as
        // `username`, `userName`, or `user`. Treat all as optional strings.
        const sessionData = sessionResult.functions as Record<string, unknown>;
        const usernameCandidate =
          (sessionData.username as string | undefined) ??
          (sessionData.userName as string | undefined) ??
          (sessionData.user as string | undefined) ??
          null;
        const username = typeof usernameCandidate === 'string' ? usernameCandidate : null;

        // Step 4 — write to the store.
        set({
          status: 'authenticated',
          isAuthenticated: true,
          username,
          permissions: resolvedPermissions,
          error: null,
        });

        // Mark this browser as "has been signed in here" so a later
        // `initAuth()` after the cookie expires takes the silent-SSO bounce
        // path instead of the first-visit short-circuit. Mirrors the write
        // that `StandaloneBootstrap` does once `GetCurrentUsername` resolves,
        // but runs unconditionally — non-SGStaff sessions also need the
        // marker, and the bootstrap effect is gated on SGStaff.
        try {
          if (username) {
            localStorage.setItem('currentUser', username);
          } else if (!localStorage.getItem('currentUser')) {
            // RoleMaster didn't return a name (some envs). Still set a marker
            // so the first-visit detector doesn't false-positive next time.
            localStorage.setItem('currentUser', '1');
          }
        } catch {
          // localStorage quota / blocked — non-fatal; worst case is the next
          // session sees /login once before bouncing.
        }

        // Step 5 — load flash-message rules for staff users (matches index.js).
        if (resolvedPermissions.SGStaff) {
          import('@/lib/flashMessageService')
            .then((m) => m.loadFlashMessageRules())
            .catch(() => {
              // Non-blocking. Flash rules are a UX nicety, not gating.
            });
        }

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Authentication failed';
        set({
          ...INITIAL_STATE,
          status: 'error',
          error: message,
        });
        return false;
      } finally {
        inFlightInit = null;
      }
    })();

    return inFlightInit;
  },

  handleSessionExpired: () => {
    clearAuthCache();
    inFlightInit = null;
    set({ ...INITIAL_STATE, status: 'unauthenticated' });
    redirectToLogin();
  },

  logout: async () => {
    // Fire the logout endpoint without awaiting completion in the happy path —
    // user feedback (the redirect) should not be gated on an external call.
    void callLogoutEndpoint();

    clearAuthCache();
    inFlightInit = null;
    // Clear the "has been signed in here" marker so a post-logout base-URL
    // visit lands on /login instead of being silently re-SSO'd into the app.
    try {
      localStorage.removeItem('currentUser');
    } catch {
      // localStorage blocked — non-fatal
    }
    set({ ...INITIAL_STATE, status: 'unauthenticated' });
    redirectToLogin();
  },

  resetAuth: () => {
    clearAuthCache();
    inFlightInit = null;
    set({ ...INITIAL_STATE });
  },

  setPermissions: (permissions) => {
    set({ permissions });
  },

  mergePermissions: (partial) => {
    const next = { ...useAuthStore.getState().permissions, ...partial };
    set({ permissions: next });
  },
}));

/** Test-only: reset module-level state. Exported via a stable name so tests can import it. */
export function __resetAuthStoreForTests(): void {
  inFlightInit = null;
  useAuthStore.setState({ ...INITIAL_STATE });
}
